import moment from "moment";

export const PRESETS = {
  LAST_3_SEASONS: "last3",
  FULL_HISTORY: "full",
  OLYMPIC_CYCLE: "olympic",
  CUSTOM_DATE: "custom",
};

export function getAvailableYears(statsData) {
  if (!statsData?.length) return [];
  return [...new Set(statsData.map((s) => s.year))].sort((a, b) => b - a);
}

/**
 * Distinct seasons (years) present in the chart source rows (team table),
 * sorted descending (most recent first).
 */
export function getSeasonsFromChartData(chartSourceData) {
  if (!chartSourceData?.length) return [];
  const years = chartSourceData
    .map((item) => moment(item.dateTimeUtc))
    .filter((date) => date.isValid())
    .map((date) => date.year());
  return [...new Set(years)].sort((a, b) => b - a);
}

/**
 * The "as of" date representing the end of a given season. For the current
 * calendar year (or beyond) we use today; for past seasons we use Dec 31.
 */
export function getSeasonEndDate(season) {
  if (season == null) return new Date();
  const currentYear = moment().year();
  if (season >= currentYear) {
    return new Date();
  }
  return moment(`${season}-12-31`, "YYYY-MM-DD").endOf("day").toDate();
}

/**
 * Build a history range object (compatible with the chart/summary filters)
 * that spans from the start of `fromSeason` to the end of `toSeason`.
 */
export function getSeasonHistoryRange(fromSeason, toSeason) {
  if (fromSeason == null || toSeason == null) return null;

  const startYear = Math.min(fromSeason, toSeason);
  const endYear = Math.max(fromSeason, toSeason);

  return {
    preset: PRESETS.CUSTOM_DATE,
    startYear,
    endYear,
    endDate: getSeasonEndDate(endYear),
  };
}

/**
 * Given the currently available seasons and the previous selection, return a
 * valid { fromSeason, toSeason } selection. Defaults to the full available
 * history when nothing is selected yet, and clamps stale values to range.
 */
export function resolveSeasonSelection(availableSeasons, fromSeason, toSeason) {
  if (!availableSeasons?.length) {
    return { fromSeason: null, toSeason: null };
  }

  const sorted = [...availableSeasons].sort((a, b) => a - b);
  const minSeason = sorted[0];
  const maxSeason = sorted[sorted.length - 1];

  let nextTo = toSeason;
  let nextFrom = fromSeason;

  if (nextTo == null) {
    nextTo = maxSeason;
  }
  if (nextFrom == null) {
    nextFrom = minSeason;
  }

  nextTo = Math.min(Math.max(nextTo, minSeason), maxSeason);
  nextFrom = Math.min(Math.max(nextFrom, minSeason), maxSeason);

  if (nextFrom > nextTo) {
    nextFrom = nextTo;
  }

  return { fromSeason: nextFrom, toSeason: nextTo };
}

/**
 * Convert a quick preset into a { fromSeason, toSeason } selection based on the
 * currently available seasons. Returns null when the preset can't be resolved.
 */
export function getSeasonRangeForPreset(preset, availableSeasons) {
  if (!availableSeasons?.length) return null;

  const { startYear, endYear } = getPresetYearRange(preset, availableSeasons);
  if (startYear == null || endYear == null) return null;

  const sorted = [...availableSeasons].sort((a, b) => a - b);
  const minSeason = sorted[0];
  const maxSeason = sorted[sorted.length - 1];

  return {
    fromSeason: Math.min(Math.max(startYear, minSeason), maxSeason),
    toSeason: Math.min(Math.max(endYear, minSeason), maxSeason),
  };
}

/**
 * Given the current season selection, return the matching quick preset id, or
 * null if the range doesn't correspond to any preset.
 */
export function findActivePresetForSeasonRange(
  fromSeason,
  toSeason,
  availableSeasons
) {
  if (fromSeason == null || toSeason == null) return null;

  const presetIds = [
    PRESETS.LAST_3_SEASONS,
    PRESETS.FULL_HISTORY,
    PRESETS.OLYMPIC_CYCLE,
  ];

  for (const preset of presetIds) {
    const range = getSeasonRangeForPreset(preset, availableSeasons);
    if (
      range &&
      range.fromSeason === fromSeason &&
      range.toSeason === toSeason
    ) {
      return preset;
    }
  }

  return null;
}

/** Human-readable label for a selected season range. */
export function formatSeasonRangeLabel(fromSeason, toSeason) {
  if (fromSeason == null && toSeason == null) return "";
  if (fromSeason == null) return String(toSeason);
  if (toSeason == null) return String(fromSeason);
  const start = Math.min(fromSeason, toSeason);
  const end = Math.max(fromSeason, toSeason);
  return start === end ? String(start) : `${start} - ${end}`;
}

/**
 * Earliest and latest available dates (as Date objects) present in the chart
 * source rows (team table). Used to bound the From/To calendar pickers.
 */
export function getChartDateBounds(chartSourceData) {
  if (!chartSourceData?.length) return null;

  const dates = chartSourceData
    .map((item) => moment(item.dateTimeUtc))
    .filter((date) => date.isValid());

  if (!dates.length) return null;

  return {
    minDate: moment.min(dates).toDate(),
    maxDate: moment.max(dates).toDate(),
  };
}

/**
 * Build a history range object (compatible with the chart/summary filters)
 * from two calendar dates. Includes both the explicit start/end dates and the
 * derived start/end years (used by the per-year aggregated stats table).
 */
export function getDateHistoryRange(fromDate, toDate) {
  if (!fromDate || !toDate) return null;

  let start = moment(fromDate);
  let end = moment(toDate);
  if (!start.isValid() || !end.isValid()) return null;

  if (start.isAfter(end)) {
    const tmp = start;
    start = end;
    end = tmp;
  }

  return {
    preset: PRESETS.CUSTOM_DATE,
    startYear: start.year(),
    endYear: end.year(),
    startDate: start.clone().startOf("day").toDate(),
    endDate: end.clone().endOf("day").toDate(),
  };
}

/**
 * Given the available date bounds and the previous selection, return a valid
 * { fromDate, toDate } selection. Defaults to the full available span when
 * nothing is selected yet, and clamps stale values back into range.
 */
export function resolveDateSelection(bounds, fromDate, toDate) {
  if (!bounds) return { fromDate: null, toDate: null };

  const min = moment(bounds.minDate);
  const max = moment(bounds.maxDate);

  let nextFrom = fromDate ? moment(fromDate) : min.clone();
  let nextTo = toDate ? moment(toDate) : max.clone();

  if (nextFrom.isBefore(min)) nextFrom = min.clone();
  if (nextFrom.isAfter(max)) nextFrom = max.clone();
  if (nextTo.isBefore(min)) nextTo = min.clone();
  if (nextTo.isAfter(max)) nextTo = max.clone();
  if (nextFrom.isAfter(nextTo)) nextFrom = nextTo.clone();

  return { fromDate: nextFrom.toDate(), toDate: nextTo.toDate() };
}

/**
 * Convert a quick preset into a { fromDate, toDate } calendar selection based
 * on the available seasons and data date bounds. Returns null when it can't be
 * resolved.
 */
export function getDateRangeForPreset(preset, availableSeasons, bounds) {
  if (!availableSeasons?.length || !bounds) return null;

  const { startYear, endYear } = getPresetYearRange(preset, availableSeasons);
  if (startYear == null || endYear == null) return null;

  const min = moment(bounds.minDate);
  const max = moment(bounds.maxDate);

  let from = moment(`${startYear}-01-01`, "YYYY-MM-DD").startOf("day");
  let to = moment(`${endYear}-12-31`, "YYYY-MM-DD").endOf("day");

  if (from.isBefore(min)) from = min.clone();
  if (from.isAfter(max)) from = max.clone();
  if (to.isAfter(max)) to = max.clone();
  if (to.isBefore(min)) to = min.clone();
  if (from.isAfter(to)) from = to.clone();

  return { fromDate: from.toDate(), toDate: to.toDate() };
}

/**
 * Given the current date selection, return the matching quick preset id, or
 * null if the range doesn't correspond to any preset.
 */
export function findActivePresetForDateRange(
  fromDate,
  toDate,
  availableSeasons,
  bounds
) {
  if (!fromDate || !toDate) return null;

  const presetIds = [
    PRESETS.LAST_3_SEASONS,
    PRESETS.FULL_HISTORY,
    PRESETS.OLYMPIC_CYCLE,
  ];

  for (const preset of presetIds) {
    const range = getDateRangeForPreset(preset, availableSeasons, bounds);
    if (
      range &&
      moment(range.fromDate).isSame(moment(fromDate), "day") &&
      moment(range.toDate).isSame(moment(toDate), "day")
    ) {
      return preset;
    }
  }

  return null;
}

/** Human-readable label for a selected calendar date range. */
export function formatDateRangeLabel(fromDate, toDate) {
  const fmt = (date) => moment(date).format("DD MMM YYYY");
  if (!fromDate && !toDate) return "";
  if (!fromDate) return fmt(toDate);
  if (!toDate) return fmt(fromDate);
  return `${fmt(fromDate)} - ${fmt(toDate)}`;
}

export function getPresetYearRange(preset, availableYears) {
  const sortedYears = [...availableYears].sort((a, b) => b - a);

  if (!sortedYears.length) {
    return { startYear: null, endYear: null };
  }

  switch (preset) {
    case PRESETS.LAST_3_SEASONS: {
      const years = sortedYears.slice(0, 3);
      return {
        startYear: Math.min(...years),
        endYear: Math.max(...years),
      };
    }
    case PRESETS.OLYMPIC_CYCLE:
      return getOlympicCycleYears(sortedYears);
    case PRESETS.FULL_HISTORY:
      return {
        startYear: Math.min(...sortedYears),
        endYear: Math.max(...sortedYears),
      };
    default:
      return { startYear: null, endYear: null };
  }
}

export function getLatestDateForPreset(preset, availableYears, chartSourceData) {
  const { startYear, endYear } = getPresetYearRange(preset, availableYears);

  const validDates = (chartSourceData || [])
    .map((item) => moment(item.dateTimeUtc))
    .filter((date) => date.isValid())
    .sort((a, b) => a.toDate() - b.toDate());

  if (!validDates.length) {
    return moment.min(moment(), moment().endOf("day")).toDate();
  }

  if (startYear == null || endYear == null) {
    return validDates[validDates.length - 1].toDate();
  }

  const inRange = validDates.filter((date) => {
    const year = date.year();
    return year >= startYear && year <= endYear;
  });

  if (inRange.length) {
    return inRange[inRange.length - 1].toDate();
  }

  const fallback = validDates.filter((date) => date.year() <= endYear);
  if (fallback.length) {
    return fallback[fallback.length - 1].toDate();
  }

  return validDates[validDates.length - 1].toDate();
}

export function findMatchingPreset(selectedDate, availableYears, chartSourceData) {
  const presetIds = [
    PRESETS.LAST_3_SEASONS,
    PRESETS.FULL_HISTORY,
    PRESETS.OLYMPIC_CYCLE,
  ];

  for (const preset of presetIds) {
    const presetEndDate = getLatestDateForPreset(
      preset,
      availableYears,
      chartSourceData
    );

    if (!moment(selectedDate).isSame(presetEndDate, "day")) {
      continue;
    }

    const presetRange = resolveHistoryRange(
      preset,
      presetEndDate,
      availableYears
    );
    const customRange = resolveHistoryRange(
      PRESETS.CUSTOM_DATE,
      selectedDate,
      availableYears
    );

    if (
      presetRange.startYear === customRange.startYear &&
      presetRange.endYear === customRange.endYear
    ) {
      return preset;
    }
  }

  return PRESETS.CUSTOM_DATE;
}

export function isActivePreset(preset) {
  return [
    PRESETS.LAST_3_SEASONS,
    PRESETS.FULL_HISTORY,
    PRESETS.OLYMPIC_CYCLE,
  ].includes(preset);
}

export function getOlympicCycleYears(availableYears) {
  if (!availableYears?.length) {
    return { startYear: null, endYear: null };
  }

  const olympicYears = availableYears.filter((year) => year % 4 === 0);
  const endYear =
    olympicYears.length > 0
      ? Math.max(...olympicYears)
      : Math.max(...availableYears);
  const startYear = endYear - 3;

  return { startYear, endYear };
}

export function resolveHistoryRange(preset, endDate, availableYears) {
  const sortedYears = [...availableYears].sort((a, b) => b - a);
  const normalizedEndDate = moment(endDate).endOf("day");

  if (!sortedYears.length) {
    return {
      preset,
      startYear: null,
      endYear: null,
      endDate: normalizedEndDate.toDate(),
    };
  }

  switch (preset) {
    case PRESETS.LAST_3_SEASONS: {
      const years = sortedYears.slice(0, 3);
      return {
        preset,
        startYear: Math.min(...years),
        endYear: Math.max(...years),
        endDate: normalizedEndDate.toDate(),
      };
    }
    case PRESETS.OLYMPIC_CYCLE: {
      const cycle = getOlympicCycleYears(sortedYears);
      return {
        preset,
        startYear: cycle.startYear,
        endYear: cycle.endYear,
        endDate: normalizedEndDate.toDate(),
      };
    }
    case PRESETS.FULL_HISTORY:
      return {
        preset,
        startYear: Math.min(...sortedYears),
        endYear: Math.max(...sortedYears),
        endDate: normalizedEndDate.toDate(),
      };
    case PRESETS.CUSTOM_DATE:
    default:
      return {
        preset: PRESETS.CUSTOM_DATE,
        startYear: Math.min(...sortedYears),
        endYear: moment(endDate).year(),
        endDate: normalizedEndDate.toDate(),
      };
  }
}

export function isWithinHistoryRange(dateValue, range) {
  if (!dateValue || !range) return false;

  const date = moment(dateValue);
  if (!date.isValid()) return false;
  if (range.endDate && date.isAfter(moment(range.endDate).endOf("day"))) {
    return false;
  }

  // When an explicit calendar start date is provided (date-range filtering),
  // use it as the lower bound; otherwise fall back to the season/year bound.
  if (range.startDate) {
    if (date.isBefore(moment(range.startDate).startOf("day"))) return false;
  } else if (range.startYear != null && date.year() < range.startYear) {
    return false;
  }

  const year = date.year();
  if (range.endYear != null && year > range.endYear) return false;

  return true;
}

export function filterStatsByHistoryRange(stats, range) {
  if (!stats?.length || !range) return [];
  return stats.filter((row) => {
    if (range.startYear != null && row.year < range.startYear) return false;
    if (range.endYear != null && row.year > range.endYear) return false;
    return true;
  });
}

export function filterTeamTableByHistoryRange(data, range) {
  if (!data?.length || !range) return [];
  return data.filter((row) => isWithinHistoryRange(row.dateTimeUtc, range));
}

export function filterChartDataByHistoryRange(chartData, range) {
  if (!chartData?.length || !range) return [];
  return chartData.filter((row) => isWithinHistoryRange(row.date, range));
}

export function computeNetWrChange(chartData) {
  if (!chartData?.length) return null;
  if (chartData.length === 1) return 0;

  const first = parseFloat(chartData[0].value);
  const last = parseFloat(chartData[chartData.length - 1].value);

  if (Number.isNaN(first) || Number.isNaN(last)) return null;
  return parseFloat((last - first).toFixed(2));
}

export function computeRankChange(chartData) {
  if (!chartData?.length) return null;
  if (chartData.length === 1) return 0;

  const startRank = parseInt(chartData[0].value, 10);
  const endRank = parseInt(chartData[chartData.length - 1].value, 10);

  if (Number.isNaN(startRank) || Number.isNaN(endRank)) return null;
  return startRank - endRank;
}

export function findWrExtremes(chartData) {
  if (!chartData?.length) {
    return { maxValue: null, minValue: null, maxIndex: null, minIndex: null };
  }

  let maxIndex = null;
  let minIndex = null;
  let maxValue = null;
  let minValue = null;

  chartData.forEach((point, index) => {
    const value = parseFloat(point.value);
    if (Number.isNaN(value)) return;

    if (maxValue === null || value > maxValue) {
      maxValue = value;
      maxIndex = index;
    } else if (value === maxValue) {
      maxIndex = index;
    }

    if (minValue === null || value < minValue) {
      minValue = value;
      minIndex = index;
    } else if (value === minValue) {
      minIndex = index;
    }
  });

  if (maxIndex !== null && maxIndex === minIndex) {
    minIndex = null;
  }

  return { maxValue, minValue, maxIndex, minIndex };
}

export function findRankExtremes(chartData) {
  if (!chartData?.length) {
    return {
      bestRank: null,
      worstRank: null,
      bestIndex: null,
      worstIndex: null,
    };
  }

  let bestIndex = null;
  let worstIndex = null;
  let bestRank = null;
  let worstRank = null;

  chartData.forEach((point, index) => {
    const rank = parseInt(point.value, 10);
    if (Number.isNaN(rank)) return;

    if (bestRank === null || rank < bestRank) {
      bestRank = rank;
      bestIndex = index;
    } else if (rank === bestRank) {
      bestIndex = index;
    }

    if (worstRank === null || rank > worstRank) {
      worstRank = rank;
      worstIndex = index;
    } else if (rank === worstRank) {
      worstIndex = index;
    }
  });

  if (bestIndex !== null && bestIndex === worstIndex) {
    worstIndex = null;
  }

  return { bestRank, worstRank, bestIndex, worstIndex };
}
